/* eslint-disable react/jsx-no-bind */
import * as Yup from "yup";
import { Button, Col, FloatingLabel, Form, Row, Spinner } from "react-bootstrap";
import { useCallback, useState } from "react";
import { Formik } from "formik";

import { passwordMinLength, passwordValidationSchema } from "../../../helpers/authHelpers";
import LwcErrorBoundary from "../../../components/LwcErrorBoundary";
import PasswordStrengthCard from "../../login/registration/PasswordStrengthCard";
import PasswordWithShow from "../../../components/PasswordWithShow";
import { useMutation } from "react-query";

import { changeMyPasswordMutation } from "../services/changePasswordMutation";
import registrationFormModel from "../../login/registration/registrationFormModel";
import { useNotifications } from "../../../components/NotificationsContext";

const schema = Yup.object().shape({
	...passwordValidationSchema(),
	oldPassword: Yup.string().required("Please enter your current password.").
		min(passwordMinLength, "Invalid password")
});
const initialValues = {
	oldPassword: "",
	password: "",
	passwordConfirm: ""
};


const {
	formField: {
		password,
		passwordConfirm
	}
} = registrationFormModel;

const ChangePasswordForm = () => {
	const { addNotification } = useNotifications();
	const [newPassword, setNewPassword] = useState("");
	const [loading, setLoading] = useState(false);

	const {
		mutateAsync: updateUserPassword
	} = changeMyPasswordMutation(useMutation);

	const handlePasswordSubmit = useCallback(async (values, actions) => {
		setLoading(true);
		try {
			const res = await updateUserPassword({
				password: values.oldPassword,
				newPassword: values.password,
				confirmNewPassword: values.passwordConfirm
			});

			if (res.success) {
				addNotification({
					message: "Password changed successfully",
					type: "success"
				});
			} else {
				throw new Error("Unknown error changing password. Please try again.");
			}
		} catch (error) {
			addNotification({
				message: error.message,
				type: "danger"
			});
		}

		actions.resetForm(initialValues);
		setLoading(false);
	}, []);

	return (
		<div className="mb-5">
			<LwcErrorBoundary>
				<h5>Change Password</h5>
				<Formik
					initialValues={initialValues}
					onSubmit={handlePasswordSubmit}
					validationSchema={schema}
				>
					{({
						errors,
						handleBlur,
						handleChange,
						handleSubmit,
						touched,
						values
					}) => (
						<Form as={Row} className="mb-4" onSubmit={handleSubmit}>
							<Col className="mb-4">
								<div className="mb-4">
									<PasswordWithShow
										// eslint-disable-next-line react/jsx-no-leaked-render
										isInvalid={touched.oldPassword && !!errors.oldPassword}
										// eslint-disable-next-line react/jsx-no-leaked-render
										isValid={touched.oldPassword && !errors.oldPassword}
										name="oldPassword"
										onBlur={handleBlur}
										onChange={handleChange}
										placeholder="Current password *"
										required
										value={values.oldPassword}
									/>

									{errors.oldPassword ? (
										<Form.Control.Feedback
											className="d-block"
											type="invalid"
										>
											{errors.oldPassword}
										</Form.Control.Feedback>) : null}
								</div>

								<div className="mb-4">
									<PasswordWithShow
										// eslint-disable-next-line react/jsx-no-leaked-render
										isInvalid={touched.password && !!errors.password}
										// eslint-disable-next-line react/jsx-no-leaked-render
										isValid={touched.password && !errors.password}
										name="password"
										onBlur={handleBlur}
										onChange={(evt) => {
											handleChange(evt);
											setNewPassword(evt.target.value);
										}}
										placeholder={password.label}
										required
										value={values.password}
									/>

									{errors.password ? (
										<Form.Control.Feedback
											className="d-block"
											type="invalid"
										>
											{errors.password}
										</Form.Control.Feedback>) : null}
								</div>

								<FloatingLabel className="mb-4" controlId={passwordConfirm.name} label={passwordConfirm.label}>
									<Form.Control
										// eslint-disable-next-line react/jsx-no-leaked-render
										isInvalid={touched.passwordConfirm && !!errors.passwordConfirm}
										// eslint-disable-next-line react/jsx-no-leaked-render
										isValid={touched.passwordConfirm && !errors.passwordConfirm}
										name="passwordConfirm"
										onBlur={handleBlur}
										onChange={handleChange}
										placeholder={passwordConfirm.label}
										required
										type="password"
										value={values.passwordConfirm}
									/>
									{errors.passwordConfirm
										? <Form.Control.Feedback type="invalid">{errors.passwordConfirm}</Form.Control.Feedback> : null}
								</FloatingLabel>

								<Button disabled={loading} onClick={handleSubmit} type="submit">
									{loading ? <Spinner animation="border" size="sm" /> : "Save New Password"}
								</Button>
							</Col>
							<Col md={6} sm={12}>
								<PasswordStrengthCard password={newPassword} />
							</Col>
						</Form>
					)}
				</Formik>
			</LwcErrorBoundary>
		</div>
	);
};

export default ChangePasswordForm;
